// テーマのカスタマイズ
// 参考: https://material-ui.com/customization/theming/
import {
  colors,
  createTheme as createMuiTheme,
  responsiveFontSizes
} from '@mui/material';

import typography from './typography';
import { THEMES } from 'src/constants';
import { dcConfig } from 'src/config';
import { softShadows } from './shadows';

const baseOptions = {
  direction: 'ltr',
  typography,
  overrides: {
    MuiLinearProgress: {
      root: {
        borderRedius: 3,
        overflow: 'hidden'
      }
    },
    MuiListItemIcon: {
      root: {
        minWidth: 32
      }
    },
    MuiChip: {
      root: {
        backgroundColor: 'rgba(0,0,0,0.075)'
      }
    }
  }
};

// データセンター別カラープリセット
const colorPresets = {
  blue: {
    lightest: 'rgba(21, 101, 192, 0.04)',
    light: colors.blue[400],
    main: colors.blue[800],
    dark: colors.blue[900]
  },
  indigo: {
    lightest: 'rgba(99, 102, 241, 0.04)',
    light: colors.indigo[400],
    main: colors.indigo[800],
    dark: colors.indigo[900]
  },
  green: {
    lightest: 'rgba(22, 163, 74, 0.04)',
    light: colors.green[400],
    main: colors.green[800],
    dark: colors.green[900]
  },
  purple: {
    lightest: 'rgba(168, 85, 247, 0.04)',
    light: colors.purple[400],
    main: colors.purple[800],
    dark: colors.purple[900]
  }
};

// 環境変数からプライマリカラーを取得（未設定時はblue）
const primaryColor =
  colorPresets[dcConfig.theme?.primaryColor] || colorPresets.blue;

const themesOptions = [
  {
    name: THEMES.DEFAULT,
    components: {
      MuiCardHeader: {
        styleOverrides: {
          root: ({ theme }) => theme.unstable_sx({ p: 1.5 })
        }
      }
    },
    overrides: {
      MuiInputBase: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: colors.blueGrey[600]
          }
        }
      }
    },
    palette: {
      mode: 'light',
      action: {
        active: colors.blueGrey[600]
      },
      background: {
        default: colors.common.white,
        dark: '#f4f6f8',
        paper: colors.common.white
      },
      primary: primaryColor,
      secondary: {
        main: colors.indigo[600]
      },
      error: {
        lightest: '#FEF3F2',
        light: '#FEE4E2',
        main: '#F04438',
        dark: '#B42318',
        darkest: '#7A271A',
        contrastText: '#FFFFFF'
      },
      warning: {
        lightest: '#FFFAEB',
        light: '#FEF0C7',
        main: '#F79009',
        dark: '#B54708',
        darkest: '#7A2E0E',
        contrastText: '#FFFFFF'
      },
      info: {
        lightest: '#ECFDFF',
        light: '#CFF9FE',
        main: '#06AED4',
        dark: '#0E7090',
        darkest: '#164C63',
        contrastText: '#FFFFFF'
      },
      success: {
        lightest: '#F0FDF9',
        light: '#3FC79A',
        main: '#10B981',
        dark: '#0B815A',
        darkest: '#134E48',
        contrastText: '#FFFFFF'
      },
      blue: {
        lightest: '#F5F8FF',
        light: '#EBEFFF',
        main: '#2970FF',
        dark: '#004EEB',
        darkest: '#00359E',
        contrastText: '#FFFFFF'
      },
      green: {
        lightest: '#F6FEF9',
        light: '#EDFCF2',
        main: '#16B364',
        dark: '#087443',
        darkest: '#084C2E',
        contrastText: '#FFFFFF'
      },
      indigo: {
        lightest: '#F5F7FF',
        light: '#EBEEFE',
        main: '#6366F1',
        dark: '#4338CA',
        darkest: '#312E81',
        contrastText: '#FFFFFF'
      },
      purple: {
        lightest: '#F9F5FF',
        light: '#F4EBFF',
        main: '#9E77ED',
        dark: '#6941C6',
        darkest: '#42307D',
        contrastText: '#FFFFFF'
      },
      text: {
        primary: colors.blueGrey[900],
        secondary: colors.blueGrey[800]
      },
      neutral: {
        main: '#64748B',
        contrastText: '#fff'
      },
      grey: {
        main: colors.grey[600],
        contrastText: '#FFFFFF'
      }
    },
    shadows: softShadows
  }
];

export const createTheme = (config = {}) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions; // themesOptions配列の1番目のオブジェクトがバインドされる
  }

  let theme = createMuiTheme({ ...baseOptions, ...themeOptions });

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
